/**
 * Evidence completeness — circular ring + missing-fields list.
 */
import { Box, Chip, Stack, Typography } from "@mui/material";
import type { ScientificCoverage } from "@flaha/shared-types";

import type { ScientificChartData } from "../types/ScientificChartTypes";
import { chartTheme, coverageRingColor } from "../utils/chartTheme";

interface EvidenceCompletenessChartProps {
  chartData: ScientificChartData<ScientificCoverage | null>;
}

const RING_SIZE = 148;
const STROKE = 12;
const R = (RING_SIZE - STROKE) / 2;
const C = 2 * Math.PI * R;

export function EvidenceCompletenessChart({ chartData }: EvidenceCompletenessChartProps) {
  const { data: coverage, accessibilitySummary } = chartData;

  if (!coverage) {
    return (
      <Typography variant="body2" color="text.secondary">
        No coverage data available.
      </Typography>
    );
  }

  const { level, modules } = coverage;
  const pct = Math.max(0, Math.min(100, level.coveragePercent));
  const offset = C * (1 - pct / 100);
  const ringColor = coverageRingColor(level.status);

  const missingFields = modules
    .filter((m) => m.required && m.missingExpectedFields.length > 0)
    .flatMap((m) =>
      m.missingExpectedFields.map((field) => ({
        module: m.label,
        field,
        status: m.status,
      }))
    );

  return (
    <Stack spacing={2.5} aria-label={accessibilitySummary} data-testid="evidence-completeness-chart">
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2.5}
        alignItems="center"
      >
        <Box
          component="svg"
          viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
          role="img"
          aria-label={`Evidence completeness ${pct.toFixed(1)} percent`}
          sx={{ width: RING_SIZE, height: RING_SIZE, flexShrink: 0 }}
          data-testid="evidence-completeness-ring"
        >
          <circle
            cx={RING_SIZE / 2}
            cy={RING_SIZE / 2}
            r={R}
            fill="none"
            stroke={chartTheme.ringTrack}
            strokeWidth={STROKE}
          />
          <circle
            cx={RING_SIZE / 2}
            cy={RING_SIZE / 2}
            r={R}
            fill="none"
            stroke={ringColor}
            strokeWidth={STROKE}
            strokeLinecap="round"
            strokeDasharray={C}
            strokeDashoffset={offset}
            transform={`rotate(-90 ${RING_SIZE / 2} ${RING_SIZE / 2})`}
            style={{ filter: `drop-shadow(0 0 8px ${ringColor}55)` }}
          />
          <text
            x={RING_SIZE / 2}
            y={RING_SIZE / 2 - 6}
            textAnchor="middle"
            fontSize={22}
            fontWeight={700}
            fill={chartTheme.label}
          >
            {pct.toFixed(0)}%
          </text>
          <text
            x={RING_SIZE / 2}
            y={RING_SIZE / 2 + 16}
            textAnchor="middle"
            fontSize={11}
            fill={chartTheme.labelMuted}
          >
            {level.status}
          </text>
        </Box>

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              label={level.declaredLevel}
              color="primary"
              variant="outlined"
            />
            <Chip
              size="small"
              label={level.status}
              color={
                level.status === "Met"
                  ? "success"
                  : level.status === "Missing"
                    ? "error"
                    : "warning"
              }
            />
          </Stack>
          <Typography
            variant="body2"
            sx={{ color: "workspace.textSecondary", mt: 1, lineHeight: 1.55 }}
          >
            {level.statement}
          </Typography>
        </Box>
      </Stack>

      <Box>
        <Typography
          variant="overline"
          sx={{ color: "workspace.textMuted", letterSpacing: "0.08em" }}
        >
          Modules
        </Typography>
        <Stack spacing={0.75} sx={{ mt: 0.75 }}>
          {modules.map((m) => (
            <Stack
              key={m.id}
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              spacing={1}
            >
              <Typography variant="body2">{m.label}</Typography>
              <Chip
                size="small"
                variant="outlined"
                label={m.status}
                color={
                  m.status === "Met"
                    ? "success"
                    : m.status === "NotRequired"
                      ? "default"
                      : "warning"
                }
                sx={{ fontSize: "0.7rem", height: 20 }}
              />
            </Stack>
          ))}
        </Stack>
      </Box>

      <Box data-testid="evidence-missing-fields">
        <Typography
          variant="overline"
          sx={{ color: "workspace.bronze", letterSpacing: "0.08em" }}
        >
          Missing expected fields
        </Typography>
        {missingFields.length === 0 ? (
          <Typography
            variant="body2"
            sx={{ color: "workspace.textMuted", mt: 0.75 }}
          >
            No required fields are missing for this test level.
          </Typography>
        ) : (
          <Stack component="ul" spacing={0.5} sx={{ m: 0, mt: 0.75, pl: 2.25 }}>
            {missingFields.map((item) => (
              <Typography
                component="li"
                key={`${item.module}-${item.field}`}
                variant="body2"
                sx={{ color: "workspace.textSecondary" }}
              >
                <strong>{item.field}</strong>
                <Box component="span" sx={{ color: "workspace.textMuted" }}>
                  {" "}
                  · {item.module}
                </Box>
              </Typography>
            ))}
          </Stack>
        )}
      </Box>
    </Stack>
  );
}
